/**
 * Goal 2: agreement orderlines save as one dialog-owned operation.
 * Dialog owns feedback — no dispatchWithToast for the same op.
 */
import {
  getOperationCoordinator,
  STEP_STATUS,
  OUTCOME,
  normalizeError,
  hasMeaningfulErrors,
  canConfirmStepFromEvidence,
  outcomeAfterRefreshFailure,
} from "../../../../operationFeedback";

export const UPDATE_AGREEMENT_ORDERLINES_OPERATION_KEY =
  "update_agreement_orderlines";

export function evaluateUpdateOrderlinesPreflight(values) {
  if (!values?.id) {
    return { ready: false, reason: "missing_id" };
  }
  return { ready: true, reason: null };
}

function defaultExecutionSteps(t) {
  return [
    {
      key: "preflight",
      label: t("operation_feedback.update_agreement_orderlines.step.preflight"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "save",
      label: t("operation_feedback.update_agreement_orderlines.step.save"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "refresh",
      label: t("operation_feedback.update_agreement_orderlines.step.refresh"),
      status: STEP_STATUS.PENDING,
    },
  ];
}

/**
 * @param {object} args
 * @param {Function} args.dispatch
 * @param {Function} args.updateAsync - updateServiceAgreementAsync
 * @param {object} args.values
 * @param {string|number} [args.companyId]
 * @param {Function} [args.onRefresh]
 * @param {Function} args.t
 */
export async function runUpdateAgreementOrderlinesOperation({
  dispatch,
  updateAsync,
  values,
  companyId,
  onRefresh,
  t,
}) {
  const coordinator = getOperationCoordinator();
  const resourceId = values?.id;
  const title = t("operation_feedback.update_agreement_orderlines.title", {
    id: resourceId || "",
  });

  const op = coordinator.start({
    operationKey: UPDATE_AGREEMENT_ORDERLINES_OPERATION_KEY,
    title,
    presentationOwner: "dialog",
    resourceId,
    companyId,
    steps: defaultExecutionSteps(t),
  });
  const id = op.clientOperationId;
  let rev = 1;

  const preflight = evaluateUpdateOrderlinesPreflight(values);

  if (
    !canConfirmStepFromEvidence({
      fromReadiness: true,
      status: STEP_STATUS.COMPLETED,
    })
  ) {
    /* intentional gate */
  }

  if (!preflight.ready) {
    const detail = t(
      "operation_feedback.update_agreement_orderlines.preflight_failed"
    );
    coordinator.updateStep(id, "preflight", {
      status: STEP_STATUS.NEEDS_FIX,
      detail,
      revision: rev++,
    });
    coordinator.updateStep(id, "save", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_RELEVANT,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: detail,
      outcome: OUTCOME.NOT_APPLIED,
    });
    return { clientOperationId: id, ok: false, error: { kind: "preflight" } };
  }

  coordinator.updateStep(id, "preflight", {
    status: STEP_STATUS.COMPLETED,
    detail: t("operation_feedback.update_agreement_orderlines.preflight_ok"),
    revision: rev++,
  });

  coordinator.updateStep(id, "save", {
    status: STEP_STATUS.RUNNING,
    revision: rev++,
  });

  let action;
  try {
    action = await dispatch(updateAsync(values));
  } catch (err) {
    const normalized = normalizeError(err, {
      operationKey: UPDATE_AGREEMENT_ORDERLINES_OPERATION_KEY,
    });
    coordinator.updateStep(id, "save", {
      status: STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: normalized.safeMessage,
      outcome: OUTCOME.NOT_APPLIED,
      requestId: normalized.requestId || null,
      error: {
        safeMessage: normalized.safeMessage,
        requestId: normalized.requestId || null,
        code: normalized.code,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized };
  }

  const payload = action?.payload;
  const rejected = action?.meta?.requestStatus === "rejected";

  if (
    rejected ||
    payload?.isAxiosError ||
    hasMeaningfulErrors(payload?.errors) ||
    payload?.error === true
  ) {
    const normalized = normalizeError(
      rejected
        ? payload ?? action?.error
        : payload?.response
          ? payload
          : { response: { status: 422, data: payload } },
      { operationKey: UPDATE_AGREEMENT_ORDERLINES_OPERATION_KEY }
    );
    coordinator.updateStep(id, "save", {
      status: STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: normalized.safeMessage,
      outcome: OUTCOME.NOT_APPLIED,
      requestId: normalized.requestId || null,
      error: {
        safeMessage: normalized.safeMessage,
        requestId: normalized.requestId || null,
        code: normalized.code,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized, payload };
  }

  coordinator.updateStep(id, "save", {
    status: STEP_STATUS.COMPLETED,
    detail: t("operation_feedback.update_agreement_orderlines.save_ok"),
    revision: rev++,
  });

  if (typeof onRefresh === "function") {
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.RUNNING,
      revision: rev++,
    });
    try {
      await Promise.resolve(onRefresh(payload));
      coordinator.updateStep(id, "refresh", {
        status: STEP_STATUS.COMPLETED,
        revision: rev++,
      });
      const afterOk = outcomeAfterRefreshFailure(STEP_STATUS.COMPLETED, false);
      coordinator.complete(id, {
        summary: t("operation_feedback.update_agreement_orderlines.success"),
        outcome: afterOk.outcome || OUTCOME.APPLIED,
      });
      return { clientOperationId: id, ok: true, payload };
    } catch (refreshErr) {
      const normalized = normalizeError(refreshErr, {
        operationKey: UPDATE_AGREEMENT_ORDERLINES_OPERATION_KEY,
      });
      const afterFail = outcomeAfterRefreshFailure(STEP_STATUS.COMPLETED, true);
      coordinator.updateStep(id, "refresh", {
        status: afterFail.refreshStatus || STEP_STATUS.FAILED,
        detail: normalized.safeMessage,
        revision: rev++,
      });
      coordinator.complete(id, {
        summary: t(
          afterFail.summaryKey || "operation_feedback.saved_list_refresh_failed"
        ),
        outcome: afterFail.outcome || OUTCOME.PARTIAL,
      });
      return { clientOperationId: id, ok: true, payload, refreshFailed: true };
    }
  }

  coordinator.updateStep(id, "refresh", {
    status: STEP_STATUS.NOT_RELEVANT,
    revision: rev++,
  });
  coordinator.complete(id, {
    summary: t("operation_feedback.update_agreement_orderlines.success"),
    outcome: OUTCOME.APPLIED,
  });
  return { clientOperationId: id, ok: true, payload };
}
